import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { backend_api } from "../../../static/server";
import { updateStatus } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import { useDialog } from "../../DialogContext";
import { useNavigate } from "react-router-dom";
import {
  appendTeamMemberLawyerId,
  compactGridStyle,
  pageScrollStyle,
} from "../../../styles/styles";
import ContactApiField from "../Settings/FieldsUi/ContactApiField";
import { generateInvoiceNumber } from "../../../pages/LawyerPages/Billing/BillingPage";
import { getCurrencySymbol } from "../../../pages/LawyerPages/Settings/CurrencySetForm";
import { isDevMode } from "../../Auth/SubscriptionContext";
import {
  isCurrencyConfigured,
  normalizeCurrencyCode,
} from "../../../utils/currencyConfig";

const DIRECT_PAYMENT_VALUE = "direct_payment";
const PAYABLE_BILL_STATUSES = new Set(["unpaid", "partial_paid"]);

const roundCurrencyAmount = (value) => {
  const numericValue = Number.parseFloat(value);
  if (Number.isNaN(numericValue)) return 0;
  return Math.round(numericValue * 100) / 100;
};

const resolveClientName = (client) => {
  if (!client) return "";
  const personName =
    `${client.first_name || ""} ${client.last_name || ""}`.trim();
  return personName || client.company_name || "";
};

const resolveBillCurrency = (bill, fallbackCurrency) =>
  bill?.currency || bill?.case?.currency || fallbackCurrency;

const resolveBillPayableAmount = (bill) => {
  if (!bill) return 0;

  const status = bill?.status;
  const totalAmount = roundCurrencyAmount(bill?.total_amount);
  const balance = roundCurrencyAmount(bill?.balance);
  const paymentReceived = roundCurrencyAmount(bill?.payment_received);

  if (status === "unpaid") {
    if (totalAmount > 0) return totalAmount;
    return Math.max(
      roundCurrencyAmount(balance || totalAmount - paymentReceived),
      0,
    );
  }

  if (status === "partial_paid") {
    if (balance > 0) return balance;
    return Math.max(roundCurrencyAmount(totalAmount - paymentReceived), 0);
  }

  return 0;
};

const isBillEligibleForPayment = (bill) => {
  if (!bill || !PAYABLE_BILL_STATUSES.has(bill?.status)) return false;
  return resolveBillPayableAmount(bill) > 0;
};

const buildStatusChipColor = (status) => {
  switch (status) {
    case "paid":
      return "success";
    case "overdue":
      return "warning";
    case "pending":
      return "info";
    case "partial_paid":
      return "warning";
    default:
      return "error";
  }
};

const RecordPaymentFrom = ({ entryData, onSuccess, billStatus }) => {
  const dispatch = useDispatch();
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const {
    token,
    defaultCurrency,
    teamMemberLawyerId,
    UserCurrencyList = [],
  } = useSelector((state) => state.user);
  const fallbackCurrency = defaultCurrency || "USD";
  const initialBill = entryData?.full_data || entryData || null;
  const initialClientId =
    initialBill?.case?.case_related_person?.id ||
    initialBill?.client?.id ||
    null;
  const initialBillId = initialBill?.id || null;

  const [lawFirmAccounts, setLawFirmAccounts] = useState([]);
  const [clientFundAccounts, setClientFundAccounts] = useState([]);
  const [clientBills, setClientBills] = useState([]);
  const [loadingLawFirmAccounts, setLoadingLawFirmAccounts] = useState(false);
  const [loadingClientFunds, setLoadingClientFunds] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastAutoDescriptionRef = useRef("");
  const previousBillIdRef = useRef(initialBillId);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      client: initialClientId,
      bill: initialBillId,
      payment_source: DIRECT_PAYMENT_VALUE,
      destination_account: null,
      reference: "",
      payer_payee: resolveClientName(
        initialBill?.case?.case_related_person || initialBill?.client,
      ),
      description: "",
      date: dayjs().format("YYYY-MM-DD"),
      amount: initialBill
        ? resolveBillPayableAmount(initialBill).toFixed(2)
        : "",
      write_off: false,
      case: initialBill?.case?.id || null,
      expenses: initialBill?.expenses || [],
      firm: initialBill?.firm?.id || null,
      currency: resolveBillCurrency(initialBill, fallbackCurrency),
    },
  });

  const client = watch("client");
  const selectedBillId = watch("bill");
  const amount = watch("amount");
  const writeOff = watch("write_off");
  const paymentSourceValue = watch("payment_source");
  const selectedCurrency = watch("currency") || fallbackCurrency;
  const currencySymbol =
    getCurrencySymbol(selectedCurrency) || selectedCurrency;

  const selectedBill = useMemo(
    () => clientBills.find((bill) => bill.id === selectedBillId) || null,
    [clientBills, selectedBillId],
  );

  const filteredLawFirmAccounts = useMemo(
    () =>
      lawFirmAccounts.filter(
        (account) =>
          normalizeCurrencyCode(account?.currency || fallbackCurrency) ===
          normalizeCurrencyCode(selectedCurrency),
      ),
    [fallbackCurrency, lawFirmAccounts, selectedCurrency],
  );

  const matchingClientFundAccounts = useMemo(
    () =>
      clientFundAccounts.filter(
        (account) =>
          normalizeCurrencyCode(account?.currency || fallbackCurrency) ===
          normalizeCurrencyCode(selectedCurrency),
      ),
    [clientFundAccounts, fallbackCurrency, selectedCurrency],
  );

  const hiddenTrustAccountCount = Math.max(
    clientFundAccounts.length - matchingClientFundAccounts.length,
    0,
  );

  const isBillCurrencyConfigured =
    !Array.isArray(UserCurrencyList) ||
    UserCurrencyList.length === 0 ||
    isCurrencyConfigured(UserCurrencyList, selectedCurrency);

  const hasBlockingPaymentCurrencyConflict =
    !isBillCurrencyConfigured || filteredLawFirmAccounts.length === 0;

  const showPaymentCurrencyNote = Boolean(
    selectedBill &&
      (hasBlockingPaymentCurrencyConflict ||
        hiddenTrustAccountCount > 0 ||
        (clientFundAccounts.length > 0 &&
          matchingClientFundAccounts.length === 0)),
  );

  const totalOpenBalance = useMemo(
    () => resolveBillPayableAmount(selectedBill),
    [selectedBill],
  );

  const numericAmount = useMemo(() => roundCurrencyAmount(amount), [amount]);

  const remainingBalance = useMemo(() => {
    if (!selectedBill) return 0;
    if (!numericAmount) return totalOpenBalance;
    return Math.max(roundCurrencyAmount(totalOpenBalance - numericAmount), 0);
  }, [numericAmount, selectedBill, totalOpenBalance]);

  const showWriteOff = Boolean(
    selectedBill && numericAmount > 0 && numericAmount < totalOpenBalance,
  );
  const writeOffAmount = writeOff ? remainingBalance : 0;
  const finalRemainingBalance = writeOff ? 0 : remainingBalance;

  const authHeaders = useMemo(
    () => ({ Authorization: `Token ${token}` }),
    [token],
  );

  const fetchAllPages = useCallback(
    async (url) => {
      const collectedResults = [];
      let nextUrl = appendTeamMemberLawyerId(url, teamMemberLawyerId);

      while (nextUrl) {
        const response = await axios.get(nextUrl, { headers: authHeaders });
        const responseData = response?.data;

        if (Array.isArray(responseData)) {
          collectedResults.push(...responseData);
          nextUrl = null;
          continue;
        }

        collectedResults.push(...(responseData?.results || []));
        nextUrl = responseData?.next
          ? appendTeamMemberLawyerId(responseData.next, teamMemberLawyerId)
          : null;
      }

      return collectedResults;
    },
    [authHeaders, teamMemberLawyerId],
  );

  const clearBillDependentFields = useCallback(() => {
    setValue("bill", null);
    setValue("case", null);
    setValue("expenses", []);
    setValue("firm", null);
    setValue("amount", "");
    setValue("write_off", false);
    setValue("currency", fallbackCurrency);
  }, [fallbackCurrency, setValue]);

  useEffect(() => {
    const fullClientObject =
      initialBill?.case?.case_related_person || initialBill?.client || null;

    reset({
      client: fullClientObject,
      bill: initialBillId,
      payment_source: DIRECT_PAYMENT_VALUE,
      destination_account: null,
      reference: "",
      payer_payee: resolveClientName(fullClientObject),
      description: "",
      date: dayjs().format("YYYY-MM-DD"),
      amount: initialBill
        ? resolveBillPayableAmount(initialBill).toFixed(2)
        : "",
      write_off: false,
      case: initialBill?.case?.id || null,
      expenses: initialBill?.expenses || [],
      firm: initialBill?.firm?.id || null,
      currency: resolveBillCurrency(initialBill, fallbackCurrency),
    });
    previousBillIdRef.current = initialBillId;
  }, [fallbackCurrency, initialBill, initialBillId, reset]);

  useEffect(() => {
    let isMounted = true;

    const loadLawFirmAccounts = async () => {
      if (!token) return;

      setLoadingLawFirmAccounts(true);

      try {
        const accounts = await fetchAllPages(
          `${backend_api}bank/accounts/?account_type=law firm&page_size=100`,
        );

        if (!isMounted) return;

        setLawFirmAccounts(accounts);

        const currentDestinationAccount = getValues("destination_account");
        if (!currentDestinationAccount) {
          const defaultAccount =
            accounts.find((account) => account.is_default) ||
            accounts[0] ||
            null;

          if (defaultAccount) {
            setValue("destination_account", defaultAccount.id);
          }
        }
      } catch (error) {
        if (isMounted) toast.error("Failed to load law firm accounts");
      } finally {
        if (isMounted) setLoadingLawFirmAccounts(false);
      }
    };

    loadLawFirmAccounts();

    return () => {
      isMounted = false;
    };
  }, [fetchAllPages, getValues, setValue, token]);

  useEffect(() => {
    let isMounted = true;

    const loadClientData = async () => {
      if (!client?.id || !token) {
        setClientFundAccounts([]);
        setClientBills([]);
        clearBillDependentFields();
        setValue("payment_source", DIRECT_PAYMENT_VALUE);
        return;
      }

      setLoadingBills(true);
      setLoadingClientFunds(true);

      try {
        const [allCandidateBills, trustAccounts] = await Promise.all([
          fetchAllPages(
            `${backend_api}firm-case/bill/?client=${client?.id}&status=${billStatus}&page_size=100`,
          ),
          fetchAllPages(
            `${backend_api}bank/accounts/?client=${client?.id}&account_type=client fund&page_size=100`,
          ),
        ]);

        if (!isMounted) return;

        const sortedOpenBills = allCandidateBills
          .filter(isBillEligibleForPayment)
          .sort((left, right) => {
            const leftDueDate = left?.due_date
              ? dayjs(left.due_date).valueOf()
              : 0;
            const rightDueDate = right?.due_date
              ? dayjs(right.due_date).valueOf()
              : 0;
            return leftDueDate - rightDueDate;
          });

        setClientBills(sortedOpenBills);
        setClientFundAccounts(trustAccounts);

        const currentBillId = getValues("bill");
        const existingSelection = sortedOpenBills.find(
          (bill) => bill.id === currentBillId,
        );
        const initialSelection = sortedOpenBills.find(
          (bill) => bill.id === initialBillId,
        );

        if (existingSelection) {
          setValue("bill", existingSelection.id, { shouldValidate: true });
        } else if (initialSelection) {
          setValue("bill", initialSelection.id, { shouldValidate: true });
        } else {
          clearBillDependentFields();
        }

        const currentPaymentSource = getValues("payment_source");
        const isValidTrustSource = trustAccounts.some(
          (account) => account.id === currentPaymentSource,
        );

        if (
          currentPaymentSource !== DIRECT_PAYMENT_VALUE &&
          !isValidTrustSource
        ) {
          setValue("payment_source", DIRECT_PAYMENT_VALUE);
        }
      } catch (error) {
        if (isMounted) {
          setClientBills([]);
          setClientFundAccounts([]);
          clearBillDependentFields();
          toast.error("Failed to load the client's open invoices");
        }
      } finally {
        if (isMounted) {
          setLoadingBills(false);
          setLoadingClientFunds(false);
        }
      }
    };

    loadClientData();

    return () => {
      isMounted = false;
    };
  }, [
    billStatus,
    clearBillDependentFields,
    client?.id,
    fetchAllPages,
    getValues,
    initialBillId,
    setValue,
    token,
  ]);

  useEffect(() => {
    if (!selectedBill) {
      previousBillIdRef.current = null;
      return;
    }

    const payableAmount = resolveBillPayableAmount(selectedBill);
    const billChanged = previousBillIdRef.current !== selectedBill.id;

    setValue("case", selectedBill?.case?.id || null);
    setValue("expenses", selectedBill?.expenses || []);
    setValue("firm", selectedBill?.firm?.id || null);
    setValue("currency", resolveBillCurrency(selectedBill, fallbackCurrency));
    setValue("write_off", false);

    if (billChanged || !getValues("amount")) {
      setValue("amount", payableAmount.toFixed(2));
    }

    if (!getValues("payer_payee")) {
      setValue(
        "payer_payee",
        resolveClientName(
          selectedBill?.case?.case_related_person || selectedBill?.client,
        ),
      );
    }

    previousBillIdRef.current = selectedBill.id;
  }, [fallbackCurrency, getValues, selectedBill, setValue]);

  useEffect(() => {
    const currentDestinationAccount = getValues("destination_account");
    const isCurrentDestinationValid = filteredLawFirmAccounts.some(
      (account) => account.id === currentDestinationAccount,
    );

    if (isCurrentDestinationValid) {
      return;
    }

    const nextDestinationAccount =
      filteredLawFirmAccounts.find((account) => account.is_default) ||
      filteredLawFirmAccounts[0] ||
      null;

    if ((nextDestinationAccount?.id || null) === (currentDestinationAccount || null)) {
      return;
    }

    setValue("destination_account", nextDestinationAccount?.id || null, {
      shouldValidate: true,
    });
  }, [filteredLawFirmAccounts, getValues, setValue]);

  useEffect(() => {
    if (paymentSourceValue === DIRECT_PAYMENT_VALUE) {
      return;
    }

    const isValidTrustSource = matchingClientFundAccounts.some(
      (account) => account.id === paymentSourceValue,
    );

    if (!isValidTrustSource) {
      setValue("payment_source", DIRECT_PAYMENT_VALUE, {
        shouldValidate: true,
      });
    }
  }, [matchingClientFundAccounts, paymentSourceValue, setValue]);

  useEffect(() => {
    const autoDescription = selectedBill
      ? `Payment for invoice ${generateInvoiceNumber(selectedBill.id)} in ${selectedCurrency}`
      : "";
    const currentDescription = getValues("description");

    if (
      !currentDescription ||
      currentDescription === lastAutoDescriptionRef.current
    ) {
      setValue("description", autoDescription);
      lastAutoDescriptionRef.current = autoDescription;
    }
  }, [getValues, selectedBill, selectedCurrency, setValue]);

  const selectedClientName = useMemo(() => {
    if (selectedBill?.case?.case_related_person) {
      return resolveClientName(selectedBill.case.case_related_person);
    }

    const firstBillClient = clientBills[0]?.case?.case_related_person;
    return (
      resolveClientName(firstBillClient) ||
      (client?.id ? `Client ${client?.id}` : "")
    );
  }, [clientBills, client?.id, selectedBill]);

  const paymentSourceOptions = useMemo(
    () => [
      { label: "Direct Payment", value: DIRECT_PAYMENT_VALUE },
      ...matchingClientFundAccounts.map((account) => ({
        label: account.account_name,
        value: account.id,
        balance: roundCurrencyAmount(account.balance),
        currency: account.currency || selectedCurrency,
      })),
    ],
    [matchingClientFundAccounts, selectedCurrency],
  );

  const accountRows = useMemo(
    () =>
      matchingClientFundAccounts.map((account) => ({
        id: account.id,
        account: account.account_name || "-",
        type: account.account_type || "-",
        amount: roundCurrencyAmount(account.balance),
        currency: account.currency || selectedCurrency,
      })),
    [matchingClientFundAccounts, selectedCurrency],
  );

  const billSummaryRows = useMemo(
    () =>
      clientBills.map((bill) => ({
        id: bill.id,
        invoice_id: generateInvoiceNumber(bill.id),
        matter: bill?.case?.case_name || "-",
        due_date: bill?.due_date
          ? dayjs(bill.due_date).format("MM/DD/YYYY")
          : "-",
        status: bill?.status || "unpaid",
        open_balance: resolveBillPayableAmount(bill),
        payment:
          bill.id === selectedBillId && amount
            ? roundCurrencyAmount(amount)
            : "-",
        currency: bill?.currency || selectedCurrency,
      })),
    [amount, clientBills, selectedBillId, selectedCurrency],
  );

  const accountColumns = useMemo(
    () => [
      { field: "account", headerName: "Account", flex: 1.2 },
      { field: "type", headerName: "Type", flex: 0.9 },
      {
        field: "amount",
        headerName: `Balance (${currencySymbol})`,
        flex: 0.9,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {currencySymbol} {roundCurrencyAmount(row.amount).toFixed(2)}
          </Typography>
        ),
      },
      { field: "currency", headerName: "Currency", flex: 0.7 },
    ],
    [currencySymbol],
  );

  const billSummaryColumns = useMemo(
    () => [
      { field: "invoice_id", headerName: "Invoice", flex: 1 },
      { field: "matter", headerName: "Matter", flex: 1.2 },
      { field: "due_date", headerName: "Due Date", flex: 0.9 },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        renderCell: ({ value }) => (
          <Chip
            label={value}
            size="small"
            color={buildStatusChipColor(value)}
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
      {
        field: "open_balance",
        headerName: `Open Balance (${currencySymbol})`,
        flex: 1,
        renderCell: ({ value }) => (
          <Typography variant="body2">
            {currencySymbol} {roundCurrencyAmount(value).toFixed(2)}
          </Typography>
        ),
      },
      {
        field: "payment",
        headerName: `Payment (${currencySymbol})`,
        flex: 1,
        renderCell: ({ value }) => (
          <Typography variant="body2">
            {typeof value === "number"
              ? `${currencySymbol} ${roundCurrencyAmount(value).toFixed(2)}`
              : "-"}
          </Typography>
        ),
      },
    ],
    [currencySymbol],
  );

  const paymentCurrencyNote = useMemo(() => {
    if (!selectedBill) return "";

    if (!isBillCurrencyConfigured) {
      return `This invoice uses ${selectedCurrency}, but that currency is not active in Billing Preferences yet. Add it there first, then create matching accounts.`;
    }

    if (filteredLawFirmAccounts.length === 0) {
      return `No law firm destination account is available in ${selectedCurrency}. Record this payment after creating a matching destination account.`;
    }

    if (
      clientFundAccounts.length > 0 &&
      matchingClientFundAccounts.length === 0
    ) {
      return `This client has trust accounts, but none of them use ${selectedCurrency}. Only matching-currency trust funds can be applied to this invoice.`;
    }

    if (hiddenTrustAccountCount > 0) {
      return `Only trust accounts in ${selectedCurrency} are shown. Accounts in other currencies are hidden to keep the payment aligned with the invoice currency.`;
    }

    return "";
  }, [
    clientFundAccounts.length,
    filteredLawFirmAccounts.length,
    hiddenTrustAccountCount,
    isBillCurrencyConfigured,
    matchingClientFundAccounts.length,
    selectedBill,
    selectedCurrency,
  ]);

  const handleOpenBillingPreferences = () => {
    closeDialog();
    navigate("/dashboard/settings/billing-preferences");
  };

  const handleOpenBankAccounts = () => {
    closeDialog();
    navigate("/dashboard/bank_accounts");
  };

  const handleRecordPayment = async (data) => {
    if (!selectedBill) {
      toast.error("Select an invoice before recording payment");
      return;
    }

    const payableAmount = resolveBillPayableAmount(selectedBill);
    const paymentAmount = roundCurrencyAmount(data.amount);

    const selectedSourceAccount =
      data.payment_source === DIRECT_PAYMENT_VALUE
        ? null
        : matchingClientFundAccounts.find(
            (account) => account.id === data.payment_source,
          ) || null;

    const destinationAccount =
      filteredLawFirmAccounts.find(
        (account) => account.id === data.destination_account,
      ) || null;

    if (!destinationAccount) {
      toast.error("Select a destination account in the same currency as the invoice");
      return;
    }

    if (paymentAmount <= 0) {
      toast.error("Enter a payment amount greater than zero");
      return;
    }

    if (paymentAmount > payableAmount) {
      toast.error("Payment amount cannot exceed the invoice open balance");
      return;
    }

    if (
      selectedSourceAccount &&
      normalizeCurrencyCode(selectedSourceAccount.currency || selectedCurrency) !==
        normalizeCurrencyCode(selectedCurrency)
    ) {
      toast.error("Selected trust account must use the same currency as the invoice");
      return;
    }

    if (
      selectedSourceAccount &&
      paymentAmount > roundCurrencyAmount(selectedSourceAccount.balance)
    ) {
      toast.error("Selected trust account does not have enough balance");
      return;
    }

    const existingPaymentReceived = roundCurrencyAmount(
      selectedBill?.payment_received || 0,
    );
    const nextPaymentReceived = roundCurrencyAmount(
      existingPaymentReceived + paymentAmount,
    );

    const nextBalance = data.write_off
      ? 0
      : Math.max(roundCurrencyAmount(payableAmount - paymentAmount), 0);

    const nextStatus = nextBalance === 0 ? "paid" : "partial_paid";

    const commonBillPayload = {
      case: selectedBill?.case?.id || data?.case,
      expenses: (selectedBill?.expenses || data?.expenses || []).map(
        (expense) => (typeof expense === "object" ? expense.id : expense),
      ),
      due_date: selectedBill?.due_date,
      firm: selectedBill?.firm?.id || data?.firm,
    };

    const updatedBillData = {
      ...commonBillPayload,
      status: nextStatus,
      payment_received: nextPaymentReceived,
      balance: nextBalance,
      write_off: Boolean(data.write_off),
    };

    const rollbackBillData = {
      ...commonBillPayload,
      status: selectedBill?.status || "unpaid",
      payment_received: existingPaymentReceived,
      balance: roundCurrencyAmount(selectedBill?.balance || 0),
      write_off: Boolean(selectedBill?.write_off),
    };

    const destinationDepositData = {
      transaction_type: "deposit",
      account: data.destination_account,
      date: data.date,
      client: data?.client?.id,
      matter: selectedBill?.case?.id || data.case,
      destination_account: data.destination_account,
      payer_payee: data.payer_payee,
      currency: destinationAccount.currency || selectedCurrency,
      amount: paymentAmount,
      description: data.description,
      status: "completed",
      ...(selectedSourceAccount
        ? { source_account: selectedSourceAccount.id }
        : { source_destination: DIRECT_PAYMENT_VALUE }),
    };

    const sourceWithdrawData = selectedSourceAccount
      ? {
          transaction_type: "withdrawal",
          account: selectedSourceAccount.id,
          date: data.date,
          client: data?.client?.id,
          matter: selectedBill?.case?.id || data.case,
          source_account: selectedSourceAccount.id,
          destination_account: data.destination_account,
          payer_payee: data.payer_payee,
          currency: selectedSourceAccount.currency || selectedCurrency,
          amount: paymentAmount,
          description: data.description,
          status: "completed",
        }
      : null;

    setSubmitting(true);

    try {
      await dispatch(
        updateStatus({
          id: selectedBill.id,
          data: updatedBillData,
          token,
        }),
      ).unwrap();

      const transactionCalls = [
        axios.post(`${backend_api}bank/transactions/`, destinationDepositData, {
          headers: authHeaders,
        }),
      ];

      if (sourceWithdrawData) {
        transactionCalls.push(
          axios.post(`${backend_api}bank/transactions/`, sourceWithdrawData, {
            headers: authHeaders,
          }),
        );
      }

      await Promise.all(transactionCalls);
      toast.success(
        `Payment recorded for ${generateInvoiceNumber(selectedBill.id)}`,
      );
      onSuccess?.();
      closeDialog();
    } catch (error) {
      try {
        await dispatch(
          updateStatus({
            id: selectedBill.id,
            data: rollbackBillData,
            token,
          }),
        ).unwrap();
      } catch (rollbackError) {
        console.error(
          "Failed to rollback invoice payment update:",
          rollbackError,
        );
      }

      console.error("Failed to record payment:", error);
      toast.error("Transaction failed. Payment was not recorded.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleRecordPayment)}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          p: "1rem",
        }}
      >
        {isDevMode && selectedBill && (
          <Box
            sx={{
              p: 2,
              bgcolor: "primary.lighter",
              borderRadius: 1,
              border: "1px solid #1976d2",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Payment Currency
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {currencySymbol} {selectedCurrency}
              </Typography>
              <Chip label={selectedCurrency} color="primary" size="small" />
            </Box>
          </Box>
        )}

        {showPaymentCurrencyNote && (
          <Alert
            severity={hasBlockingPaymentCurrencyConflict ? "warning" : "info"}
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                {!isBillCurrencyConfigured && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleOpenBillingPreferences}
                  >
                    Billing Preferences
                  </Button>
                )}
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleOpenBankAccounts}
                >
                  Bank Accounts
                </Button>
              </Box>
            }
          >
            {paymentCurrencyNote}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1rem",
          }}
        >
          <ContactApiField
            name="client"
            label="Client"
            control={control}
            token={token}
            apiUrl={`${backend_api}contact/retrieve/`}
            isRequired={true}
            errors={errors}
            placeholder="Select client"
            returnFullData={true}
            teamMemberLawyerId={teamMemberLawyerId}
            disabled={submitting}
          />
          <Controller
            name="bill"
            control={control}
            rules={{ required: "Select an invoice" }}
            render={({ field }) => (
              <FormControl fullWidth>
                <Autocomplete
                  disablePortal
                  disabled={!client?.id || loadingBills || submitting}
                  loading={loadingBills}
                  options={clientBills}
                  getOptionLabel={(option) =>
                    `${generateInvoiceNumber(option.id)} - ${option?.case?.case_name || "Matter"}`
                  }
                  value={
                    clientBills.find((bill) => bill.id === field.value) || null
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  onChange={(_, selectedOption) =>
                    field.onChange(selectedOption?.id || null)
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {generateInvoiceNumber(option.id)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {option?.case?.case_name || "No matter linked"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            flexShrink: 0,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getCurrencySymbol(
                              resolveBillCurrency(option, fallbackCurrency),
                            ) ||
                              resolveBillCurrency(
                                option,
                                fallbackCurrency,
                              )}{" "}
                            {resolveBillPayableAmount(option).toFixed(2)}
                          </Typography>
                          <Chip
                            label={option?.status || "unpaid"}
                            size="small"
                            color={buildStatusChipColor(option?.status)}
                            sx={{ textTransform: "capitalize" }}
                          />
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          Invoice
                          <span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      placeholder={
                        client?.id
                          ? "Select unpaid or partially paid invoice"
                          : "Select client first"
                      }
                      size="small"
                      error={Boolean(errors.bill)}
                      helperText={
                        errors.bill?.message ||
                        (!loadingBills && client?.id && clientBills.length === 0
                          ? "No unpaid or partially paid invoices found for this client"
                          : "")
                      }
                    />
                  )}
                />
              </FormControl>
            )}
          />
        </Box>

        {client?.id && matchingClientFundAccounts.length > 0 && (
          <Accordion
            defaultExpanded
            sx={{ p: 0, boxShadow: "none", border: "1px solid #e5e5e5" }}
          >
            <AccordionSummary
              expandIcon={
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: "1rem", m: 0 }} />
              }
              sx={{
                px: "0.5rem",
                minHeight: "1.5rem !important",
                height: "1.5rem",
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                  minHeight: "1.5rem",
                },
              }}
            >
              <Typography
                component="span"
                sx={{ fontSize: "0.8rem", fontWeight: 600 }}
              >
                Available Trust Funds for {selectedClientName} (
                {selectedCurrency})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <DataGrid
                rows={accountRows}
                columns={accountColumns}
                loading={loadingClientFunds}
                autoHeight
                hideFooter
                pageSizeOptions={[5]}
                disableRowSelectionOnClick
                sx={compactGridStyle}
              />
            </AccordionDetails>
          </Accordion>
        )}

        {client?.id && (
          <>
            {loadingBills || clientBills.length > 0 ? (
              <Accordion
                defaultExpanded
                sx={{ p: 0, boxShadow: "none", border: "1px solid #e5e5e5" }}
              >
                <AccordionSummary
                  expandIcon={
                    <KeyboardArrowDownRoundedIcon
                      sx={{ fontSize: "1rem", m: 0 }}
                    />
                  }
                  sx={{
                    px: "0.5rem",
                    minHeight: "1.5rem !important",
                    height: "1.5rem",
                    "& .MuiAccordionSummary-content": {
                      margin: 0,
                      minHeight: "1.5rem",
                    },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.8rem", fontWeight: 600 }}
                  >
                    Open Invoices ({clientBills.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <DataGrid
                    rows={billSummaryRows}
                    columns={billSummaryColumns}
                    loading={loadingBills}
                    autoHeight
                    hideFooter
                    pageSizeOptions={[5]}
                    onRowClick={(params) =>
                      setValue("bill", params.row.id, { shouldValidate: true })
                    }
                    disableRowSelectionOnClick
                    sx={{
                      ...compactGridStyle,
                      "& .MuiDataGrid-row": { cursor: "pointer" },
                      "& .MuiDataGrid-row.selected-bill": {
                        bgcolor: "#fff7e0",
                      },
                    }}
                    getRowClassName={(params) =>
                      params.id === selectedBillId ? "selected-bill" : ""
                    }
                  />
                </AccordionDetails>
              </Accordion>
            ) : (
              <Box
                sx={{
                  border: "1px dashed #d0d5dd",
                  borderRadius: "0.5rem",
                  p: "0.9rem 1rem",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No unpaid or partially paid invoices were found for this
                  client.
                </Typography>
              </Box>
            )}
          </>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1rem",
          }}
        >
          <Controller
            name="destination_account"
            control={control}
            rules={{ required: "Destination account is required" }}
            render={({ field }) => (
              <FormControl fullWidth>
                <Autocomplete
                  disablePortal
                  disabled={loadingLawFirmAccounts || submitting}
                  loading={loadingLawFirmAccounts}
                  options={filteredLawFirmAccounts}
                  getOptionLabel={(option) => option?.account_name || ""}
                  value={
                    filteredLawFirmAccounts.find(
                      (account) => account.id === field.value,
                    ) || null
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  onChange={(_, selectedOption) =>
                    field.onChange(selectedOption?.id || null)
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {option.account_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {option.currency || selectedCurrency}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {option.is_default && (
                            <Chip
                              label="Default"
                              size="small"
                              color="primary"
                            />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getCurrencySymbol(option.currency) ||
                              option.currency}{" "}
                            {roundCurrencyAmount(option.balance).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          Destination Account
                          <span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      size="small"
                      error={Boolean(errors.destination_account)}
                      helperText={
                        errors.destination_account?.message ||
                        (selectedBill && filteredLawFirmAccounts.length === 0
                          ? `No destination accounts are available in ${selectedCurrency}`
                          : "")
                      }
                    />
                  )}
                />
              </FormControl>
            )}
          />
          <Controller
            name="reference"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={submitting}
                label="Reference ID"
                size="small"
                fullWidth
              />
            )}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1rem",
          }}
        >
          <Controller
            name="payment_source"
            control={control}
            rules={{ required: "Payment source is required" }}
            render={({ field }) => (
              <FormControl fullWidth>
                <Autocomplete
                  disablePortal
                  disabled={!client?.id || submitting}
                  loading={loadingClientFunds}
                  options={paymentSourceOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    paymentSourceOptions.find(
                      (option) => option.value === field.value,
                    ) || null
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === value?.value
                  }
                  onChange={(_, selectedOption) =>
                    field.onChange(selectedOption?.value || null)
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.value}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2">
                            {option.label}
                          </Typography>
                          {option.value !== DIRECT_PAYMENT_VALUE && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {option.currency || selectedCurrency}
                            </Typography>
                          )}
                        </Box>
                        {option.value !== DIRECT_PAYMENT_VALUE && (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {getCurrencySymbol(option.currency) ||
                              option.currency}{" "}
                            {roundCurrencyAmount(option.balance).toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        <span>
                          Payment Source
                          <span style={{ color: "red" }}> *</span>
                        </span>
                      }
                      size="small"
                      error={Boolean(errors.payment_source)}
                      helperText={
                        errors.payment_source?.message ||
                        (clientFundAccounts.length > 0 &&
                        matchingClientFundAccounts.length === 0
                          ? `No trust accounts are available in ${selectedCurrency}`
                          : "")
                      }
                    />
                  )}
                />
              </FormControl>
            )}
          />
          <Controller
            name="date"
            control={control}
            rules={{ required: "Payment date is required" }}
            render={({ field }) => (
              <DatePicker
                disabled={submitting}
                value={field.value ? dayjs(field.value) : null}
                onChange={(newValue) =>
                  field.onChange(
                    newValue && dayjs(newValue).isValid()
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : null,
                  )
                }
                format="MM/DD/YYYY"
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    label: (
                      <span>
                        Payment Date
                        <span style={{ color: "red" }}> *</span>
                      </span>
                    ),
                    size: "small",
                    error: Boolean(errors.date),
                    helperText: errors.date?.message,
                  },
                  popper: {
                    sx: {
                      "& .MuiPickersPopper-root": {
                        maxWidth: "100%",
                        maxHeight: "100%",
                        overflow: "hidden",
                      },
                      "& .MuiPaper-root": {
                        width: "fit-content",
                        maxHeight: "15rem",
                        overflowY: "auto",
                        ...pageScrollStyle,
                      },
                    },
                  },
                }}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "1rem",
          }}
        >
          <Controller
            name="payer_payee"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={submitting}
                label="Payer"
                size="small"
                fullWidth
              />
            )}
          />
          <Controller
            name="amount"
            control={control}
            rules={{
              required: "Payment amount is required",
              validate: {
                invoice: () =>
                  Boolean(selectedBill) || "Select an invoice first",
                positive: (value) =>
                  roundCurrencyAmount(value) > 0 ||
                  "Enter an amount greater than zero",
                withinBalance: (value) =>
                  roundCurrencyAmount(value) <= totalOpenBalance ||
                  `Amount cannot exceed ${currencySymbol} ${totalOpenBalance.toFixed(2)}`,
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={!selectedBill || submitting}
                type="number"
                label={
                  <span>
                    Payment Amount
                    <span style={{ color: "red" }}> *</span>
                  </span>
                }
                size="small"
                fullWidth
                error={Boolean(errors.amount)}
                helperText={errors.amount?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currencySymbol}
                    </InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, max: totalOpenBalance, step: 0.01 }}
              />
            )}
          />
        </Box>

        <Box>
          <Box
            sx={{
              border: "1px solid #0000003B",
              borderRadius: "0.3rem 0.3rem 0rem 0rem",
              width: "100%",
              p: "0.75rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.65rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Total open balance
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {currencySymbol} {totalOpenBalance.toFixed(2)}
              </Typography>
            </Box>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Remaining after payment
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {currencySymbol} {finalRemainingBalance.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {showWriteOff && (
            <Box
              sx={{
                borderLeft: "1px solid #0000003B",
                borderRight: "1px solid #0000003B",
                borderBottom: "1px solid #0000003B",
                borderRadius: "0rem 0rem 0.3rem 0.3rem",
                px: "0.75rem",
                py: "0.5rem",
              }}
            >
              <Controller
                name="write_off"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    }
                    label="Write off the remaining balance on this invoice"
                    sx={{
                      alignItems: "center",
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.85rem",
                        color: "text.secondary",
                      },
                    }}
                  />
                )}
              />

              {writeOff && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Write-off amount
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {currencySymbol} {writeOffAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              disabled={submitting}
              multiline
              rows={2}
              fullWidth
              label="Description"
              placeholder="Add a note for this payment"
            />
          )}
        />
      </Box>

      <Box
        sx={{
          height: "fit-content",
          borderTop: "1px solid #ddd",
          position: "sticky",
          bottom: 0,
          backdropFilter: "blur(10px)",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          p: "0.5rem",
          zIndex: 1000,
          gap: "0.5rem",
        }}
      >
        <Button
          size="small"
          variant="outlined"
          disabled={submitting}
          onClick={() => closeDialog()}
        >
          Cancel
        </Button>
        <LoadingButton
          size="small"
          variant="contained"
          color="primary"
          type="submit"
          loading={submitting}
          disabled={!selectedBill || loadingBills}
        >
          Record Payment
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default RecordPaymentFrom;
